import { TokenQuantity } from './chunk-5PIFEZWT.js';
import { ProtocolBadge } from './chunk-O26G5KPD.js';
import { useProtocolUi, fieldAtPath, SectionCard, FactGrid, ProtocolDisclosure, ProtocolParamList, ProtocolValue, isRecord } from './chunk-NNFGQV3X.js';
import { resolveChain, isValidAddress } from '@authe/fe-commons/chains';
import { decodedOf, rawOf, displayOf } from '@authe/fe-commons/storage';
import { jsx, jsxs } from 'react/jsx-runtime';

// src/protocols/across/labels.ts
var ACROSS = { protocolId: "across"};
var ACROSS_CALL_SIGNATURES = [
  "deposit(bytes32,bytes32,bytes32,bytes32,uint256,uint256,uint256,bytes32,uint32,uint32,uint32,bytes)",
  "depositNow(bytes32,bytes32,bytes32,bytes32,uint256,uint256,uint256,bytes32,uint32,uint32,bytes)",
  "depositV3(address,address,address,address,uint256,uint256,uint256,address,uint32,uint32,uint32,bytes)",
  "depositV3Now(address,address,address,address,uint256,uint256,uint256,address,uint32,uint32,bytes)",
  "unsafeDeposit(bytes32,bytes32,bytes32,bytes32,uint256,uint256,uint256,bytes32,uint256,uint32,uint32,uint32,bytes)",
  "fillRelay((bytes32,bytes32,bytes32,bytes32,bytes32,uint256,uint256,uint256,uint256,uint32,uint32,bytes),uint256,bytes32)",
  "fillV3Relay((address,address,address,address,address,uint256,uint256,uint256,uint32,uint32,uint32,bytes),uint256)"
];
var ACROSS_FILL_EVENTS = ["FilledRelay", "FilledV3Relay"];
var ACROSS_EVENT_SIGNATURES = [
  "FilledRelay(bytes32,bytes32,uint256,uint256,uint256,uint256,uint256,uint32,uint32,bytes32,bytes32,bytes32,bytes32,bytes32,(bytes32,bytes32,uint256,uint8))",
  "FilledV3Relay(address,address,uint256,uint256,uint256,uint256,uint32,uint32,uint32,address,address,address,address,bytes,(address,bytes,uint256,uint8))",
  "FundsDeposited(bytes32,bytes32,uint256,uint256,uint256,uint256,uint32,uint32,uint32,bytes32,bytes32,bytes32,bytes)",
  "V3FundsDeposited(address,address,uint256,uint256,uint256,uint32,uint32,uint32,uint32,address,address,address,bytes)"
];
function chainOf(value) {
  const decoded = decodedOf(value);
  if (isRecord(decoded) && typeof decoded.chain_id === "string") return decoded.chain_id;
  const raw = rawOf(value);
  return raw == null ? void 0 : resolveChain(String(raw))?.caip2 ?? void 0;
}
function acrossScopedParams(params, fill, localChain, artifactName) {
  const get = (name) => fieldAtPath(params, name, `relayData.${name}`);
  const origin = fill ? chainOf(get("originChainId")) : localChain;
  const destination = fill ? localChain : chainOf(get("destinationChainId"));
  const chains = {
    depositor: origin,
    inputToken: origin,
    recipient: destination,
    outputToken: destination,
    updatedRecipient: destination,
    exclusiveRelayer: destination,
    // FilledRelay's bytes32 relayer is the solver's requested repayment address.
    relayer: artifactName === "FilledV3Relay" ? localChain : chainOf(get("repaymentChainId")),
    repaymentAddress: chainOf(get("repaymentChainId"))
  };
  const actor = (value, chain) => {
    const raw = rawOf(value);
    const display = displayOf(value);
    let address = typeof display === "string" ? display : raw;
    if (typeof address === "string" && chain?.startsWith("eip155:") && /^0x0{24}[0-9a-f]{40}$/i.test(address)) address = `0x${address.slice(-40)}`;
    return { "@decoded": chain && typeof address === "string" && isValidAddress(address, chain) ? { kind: "address", raw, display: address, decoded: { chain_id: chain } } : { kind: "bytes", raw, display: raw } };
  };
  const fields = (value) => {
    if (!isRecord(value)) return value;
    if (isRecord(value["@decoded"])) {
      const envelope = value["@decoded"];
      return { ...value, "@decoded": {
        ...envelope,
        display: fields(envelope.display ?? envelope.raw),
        decoded: fields(envelope.decoded ?? (isRecord(envelope.raw) ? envelope.raw : void 0))
      } };
    }
    return Object.fromEntries(Object.entries(value).map(([name, item]) => [
      name,
      Object.hasOwn(chains, name) ? actor(item, chains[name]) : item
    ]));
  };
  return params.map((param) => {
    const value = Object.hasOwn(chains, param.name) ? actor(param.value, chains[param.name]) : ["relayData", "relayExecutionInfo"].includes(param.name) ? fields(param.value) : param.value;
    return value === param.value ? param : { ...param, value, display: void 0, semanticType: void 0 };
  });
}
function AcrossTransfer({ params, fill, receipt, name, address }) {
  const { chain } = useProtocolUi();
  const artifactName = name?.split("(")[0];
  params = acrossScopedParams(params, fill, chain, artifactName);
  const get = (name2) => fieldAtPath(params, name2, `relayData.${name2}`);
  const fact = (label, value) => ({
    label,
    value: /* @__PURE__ */ jsx(ProtocolValue, { value }),
    when: value != null
  });
  const quantity = (label, value) => ({
    label,
    value: /* @__PURE__ */ jsx(TokenQuantity, { value }),
    when: value != null
  });
  const facts = [
    fact(receipt ? "Event" : "Function", name),
    fact(receipt ? "Emitting SpokePool" : "SpokePool", address),
    fact("Deposit ID (scoped to origin chain)", get("depositId")),
    fact(fill ? "Origin chain" : "Destination chain", get(fill ? "originChainId" : "destinationChainId")),
    fact("Depositor", get("depositor")),
    fact("Original recipient", get("recipient")),
    fact("Origin token", get("inputToken")),
    quantity("Origin amount", get("inputAmount")),
    fact("Destination token", get("outputToken")),
    quantity("Quoted destination amount", get("outputAmount"))
  ];
  if (fill) facts.push(
    fact("Executed recipient", fieldAtPath(params, "relayExecutionInfo.updatedRecipient")),
    quantity("Executed output amount", fieldAtPath(params, "relayExecutionInfo.updatedOutputAmount")),
    fact("Fill type", fieldAtPath(params, "relayExecutionInfo.fillType")),
    fact(artifactName === "FilledV3Relay" ? "Fill-executing relayer" : "Solver repayment address (relayer)", get("relayer")),
    fact("Solver repayment chain", get("repaymentChainId")),
    fact("Solver repayment address", get("repaymentAddress"))
  );
  else facts.push(fact("Fill deadline", get("fillDeadline")));
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      protocolId: ACROSS.protocolId,
      title: `Across ${fill ? "fill" : "deposit"} ${receipt ? "receipt" : "request"}`,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: ACROSS.protocolId }),
      children: [
        /* @__PURE__ */ jsx("p", { className: "mb-3 text-xs text-muted-foreground", children: receipt ? fill ? "This receipt records a fill. Executed terms can differ from the original quote. Recipient transfers and callback execution establish the final application outcome." : "This receipt records the source deposit. A matching destination fill establishes delivery." : "These are submitted call terms. A call can revert; use committed receipts to establish the deposit or fill." }),
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        fill && /* @__PURE__ */ jsx("p", { className: "mt-3 text-xs text-muted-foreground", children: "The repayment chain is the solver\u2019s reimbursement choice, not the user\u2019s transfer destination. A fill does not prove that repayment occurred." }),
        /* @__PURE__ */ jsx("p", { className: "mt-3 text-xs text-muted-foreground", children: "Deposit, fill, wrapping and forwarding records can describe stages of the same transfer. Their amounts are not additive bridge volume." }),
        /* @__PURE__ */ jsx(ProtocolDisclosure, { className: "mt-3", summary: receipt ? "All event fields" : "All call arguments", children: /* @__PURE__ */ jsx(ProtocolParamList, { params, emptyLabel: "No decoded fields" }) })
      ]
    }
  );
}
function AcrossTransferCall({ call }) {
  return /* @__PURE__ */ jsx(
    AcrossTransfer,
    {
      params: call.params,
      fill: call.label?.startsWith("fill") ?? false,
      receipt: false,
      name: call.signature ?? call.label,
      address: call.address
    }
  );
}
function AcrossTransferEvent({ event }) {
  return /* @__PURE__ */ jsx(
    AcrossTransfer,
    {
      params: event.params,
      fill: ACROSS_FILL_EVENTS.some((name) => name === event.name),
      receipt: true,
      name: event.signature ?? event.name,
      address: event.address
    }
  );
}

// src/protocols/across/index.ts
var ACROSS_HINTS = {
  valueHints: [],
  callHints: ACROSS_CALL_SIGNATURES.map((signature) => ({
    protocolId: ACROSS.protocolId,
    call: signature.slice(0, signature.indexOf("(")),
    signature,
    component: "AcrossTransferCall",
    summaryFields: []
  })),
  eventHints: ACROSS_EVENT_SIGNATURES.map((signature) => ({
    protocolId: ACROSS.protocolId,
    event: signature.slice(0, signature.indexOf("(")),
    signature,
    component: "AcrossTransferEvent",
    summaryFields: []
  }))
};
function registerAcross(registry) {
  registry.registerCall("AcrossTransferCall", AcrossTransferCall);
  registry.registerEvent("AcrossTransferEvent", AcrossTransferEvent);
  registry.registerHints(ACROSS_HINTS);
  return registry;
}

export { ACROSS_HINTS, registerAcross };
//# sourceMappingURL=chunk-OOIP5T73.js.map
//# sourceMappingURL=chunk-OOIP5T73.js.map